import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BellRing, KeyRound, LogOut, Monitor, Moon, Sun, Trash2 } from 'lucide-react';
import { Button, Card, cn, TextField } from '@tcrd/ui';
import { api, errorMessage } from '../../lib/api';
import { IosInstallHint, needsIosInstall } from '../../components/IosInstallHint';
import { env } from '../../lib/env';
import { enableWebPush, unregisterNativePush } from '../../lib/push';
import { useToast } from '../../components/toast';
import { useAuth } from '../../stores/auth';
import { useTheme, type ThemeMode } from '../../stores/theme';
import { getLang, setLang } from '../../i18n/translator';

const modes: Array<{ id: ThemeMode; label: string; icon: typeof Sun }> = [
  { id: 'light', label: 'Claro', icon: Sun },
  { id: 'dark', label: 'Oscuro', icon: Moon },
  { id: 'system', label: 'Sistema', icon: Monitor },
];

/** Tema, cierre de sesión y eliminación de cuenta, compartido por todos los roles. */
export function AccountSettings() {
  const { mode, setMode } = useTheme();
  const logout = useAuth((s) => s.logout);
  const history = useHistory();
  const toast = useToast();
  const hasPassword = useAuth((s) => s.user?.hasPassword ?? true);

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-4">
        <p className="text-sm font-semibold mb-3">Apariencia</p>
        <div role="radiogroup" aria-label="Tema" className="grid grid-cols-3 gap-2">
          {modes.map((m) => (
            <button
              key={m.id}
              role="radio"
              aria-checked={mode === m.id}
              onClick={() => setMode(m.id)}
              className={cn(
                'tap flex flex-col items-center gap-1 rounded-2xl border py-3 text-sm',
                mode === m.id ? 'border-brand-red bg-brand-red/5 font-semibold' : 'border-[var(--border)]',
              )}
            >
              <m.icon className="size-5" aria-hidden />
              {m.label}
            </button>
          ))}
        </div>
      </Card>
      <Card className="p-4">
        <p className="text-sm font-semibold mb-3">Idioma</p>
        <div role="radiogroup" aria-label="Idioma" className="grid grid-cols-2 gap-2" data-no-translate>
          {([
            ['es', 'Español'],
            ['en', 'English'],
          ] as const).map(([l, label]) => (
            <button key={l} role="radio" aria-checked={getLang() === l} onClick={() => getLang() !== l && setLang(l)} className={cn('tap rounded-2xl border py-3 text-sm', getLang() === l ? 'border-brand-red bg-brand-red/5 font-semibold' : 'border-[var(--border)]')}>
              {label}
            </button>
          ))}
        </div>
      </Card>
      {needsIosInstall() && <IosInstallHint />}
      {!env.isNative && !needsIosInstall() && (
        <Card className="p-4 flex items-center gap-3">
          <BellRing className="size-5 text-[var(--text-muted)]" aria-hidden />
          <div className="flex-1">
            <p className="text-sm font-semibold">Notificaciones del navegador</p>
            <p className="text-xs text-[var(--text-muted)]">Recibe avisos aunque la pestaña esté cerrada (si tu navegador lo permite).</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={async () => {
              const r = await enableWebPush().catch(() => 'unsupported' as const);
              toast(
                r === 'enabled' ? 'Notificaciones activadas' : r === 'denied' ? 'Permiso denegado en el navegador' : r === 'not-configured' ? 'Las notificaciones web aún no están configuradas en el servidor' : 'Este navegador no admite notificaciones. Prueba con Chrome o instala la app.',
                r === 'enabled' ? 'success' : 'dark',
              );
            }}
          >
            Activar
          </Button>
        </Card>
      )}
      {hasPassword && <ChangePassword />}
      <Button
        variant="danger"
        block
        icon={<LogOut className="size-5" />}
        onClick={async () => {
          await unregisterNativePush();
          await logout();
          history.replace('/');
        }}
      >
        Cerrar sesión
      </Button>
      <DeleteAccount />
    </div>
  );
}

/** Eliminación de cuenta: anonimiza los datos personales; los viajes y pagos quedan como registro. */
function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const logout = useAuth((s) => s.logout);
  // Las cuentas creadas con Google no tienen contraseña: se confirma escribiendo ELIMINAR.
  const hasPassword = useAuth((s) => s.user?.hasPassword ?? true);
  const ready = hasPassword ? !!password : password.trim().toUpperCase() === 'ELIMINAR';
  const history = useHistory();
  const toast = useToast();

  if (!open) {
    return (
      <button
        type="button"
        className="tap w-full h-12 rounded-xl border-2 border-brand-red text-brand-red font-semibold flex items-center justify-center gap-2 hover:bg-brand-red/5"
        onClick={() => setOpen(true)}
      >
        <Trash2 className="size-5" aria-hidden /> Eliminar mi cuenta
      </button>
    );
  }
  const confirm = async () => {
    setBusy(true);
    try {
      await api('/users/me/delete', { body: hasPassword ? { password } : { confirm: password } });
      await unregisterNativePush().catch(() => undefined);
      await logout().catch(() => undefined);
      toast('Tu cuenta fue eliminada', 'success');
      history.replace('/');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-4 flex flex-col gap-3 border-brand-red/40">
      <p className="text-sm font-semibold">Eliminar mi cuenta</p>
      <p className="text-xs text-[var(--text-muted)]">
        Se borran tu nombre, correo, teléfono, foto, direcciones y notificaciones, y se cierran todas tus sesiones. Los viajes y pagos se conservan sin tus datos personales como registro contable. Esta acción no se puede deshacer.
      </p>
      {hasPassword ? (
        <TextField label="Confirma con tu contraseña" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      ) : (
        <TextField label="Escribe ELIMINAR para confirmar" autoCapitalize="characters" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
        <Button variant="danger" icon={<Trash2 className="size-5" />} loading={busy} disabled={!ready} onClick={confirm}>Eliminar</Button>
      </div>
    </Card>
  );
}

/** Cambio de contraseña (obligado tras recibir una contraseña temporal). Cierra las demás sesiones. */
function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const valid = next.length >= 10 && /[A-Za-z]/.test(next) && /\d/.test(next);

  if (!open) {
    return (
      <Button variant="secondary" block icon={<KeyRound className="size-5" />} onClick={() => setOpen(true)}>
        Cambiar contraseña
      </Button>
    );
  }
  const save = async () => {
    setBusy(true);
    try {
      await api('/auth/change-password', { body: { currentPassword: current, newPassword: next } });
      toast('Contraseña actualizada. Se cerraron tus otras sesiones.', 'success');
      setOpen(false);
      setCurrent('');
      setNext('');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-4 flex flex-col gap-3">
      <p className="text-sm font-semibold">Cambiar contraseña</p>
      <TextField label="Contraseña actual" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      <TextField label="Nueva contraseña" hint="Mínimo 10 caracteres, con letras y números" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
        <Button loading={busy} disabled={!current || !valid} onClick={save}>Guardar</Button>
      </div>
    </Card>
  );
}
